export const rowsFromTable = (table) => {
  if (!table) return [];
  const cols = table.schema.fields.map((f) => f.name);
  const rows = [];
  for (let i = 0; i < table.numRows; i++) {
    const obj = {};
    for (const c of cols) obj[c] = table.getChild(c).get(i);
    rows.push(obj);
  }
  return rows;
};

// NZ uses area_id (integer) as the join key for all three levels.
// area_cd is set to String(area_id) so restyleGeo's getRowId works uniformly.
export const normalizeId = (_level, props) => {
  const v = props?.area_id ?? props?.AREA_ID;
  return v != null ? String(v) : null;
};

export const clamp01 = (x) => Math.max(0, Math.min(1, x));

export const hexToHsl = (hex) => {
  let c = hex.replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

export const hslToHex = (h, s, l) => {
  h /= 360; s /= 100; l /= 100;
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("").toUpperCase();
};

export const makeHeatScale = (baseHex, values) => {
  const finite = values.filter((v) => Number.isFinite(v));
  const min = finite.length ? Math.min(...finite) : 0;
  const max = finite.length ? Math.max(...finite) : 100;
  const { h, s } = hexToHsl(baseHex);
  return (v) => {
    if (!Number.isFinite(v)) return "#CCCCCC";
    const t = clamp01((v - min) / Math.max(1e-6, max - min));
    return hslToHex(h, s, 95 - t * 60);
  };
};

export const make2pointHeatScale = (baseHexPos, baseHexNeg, values) => {
  const finite = values.filter((v) => Number.isFinite(v));
  const min = finite.length ? Math.min(...finite) : -100;
  const max = finite.length ? Math.max(...finite) : 100;
  return (v) => {
    if (!Number.isFinite(v)) return "#CCCCCC";
    const t = v >= 0 ? v / Math.max(1e-6, max) : v / Math.min(-1e-6, min);
    const { h, s } = hexToHsl(v >= 0 ? baseHexPos : baseHexNeg);
    return hslToHex(h, s, 95 - clamp01(Math.abs(t)) * 60);
  };
};

const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];

export const buildLabelsAndColors = (groupsById, _theme_colours) => {
  const labels = {};
  const colors = {};
  Object.values(groupsById || {}).forEach((g) => {
    if (!g.keys) return;
    g.keys.forEach((k) => {
      labels[k] = g.labels?.[k] || k;
      if (g.colors?.[k]) {
        colors[k] = g.colors[k];
      } else if (g.type === "likert" && g.polarity) {
        const pol = g.polarity;
        if (toArr(pol.pos).includes(k))      colors[k] = "#2e7d32";
        else if (toArr(pol.neg).includes(k)) colors[k] = "#c62828";
        else                                  colors[k] = "#9ca3af";
      } else {
        colors[k] = "#888";
      }
    });
  });
  return { labels, colors };
};
